import { useEffect, useState, type JSX, type ReactNode } from 'react';

export type ConfigErrorVariant =
  | 'offline'
  | 'serverError'
  | 'invalidConfig'
  | 'tampered'
  | 'crash';

export interface ConfigErrorScreenProps {
  /** Visual + copy variant. Drives the icon and the friendly title/body. */
  variant?: ConfigErrorVariant;
  /** Short, customer-facing title. Defaults per `variant`. */
  title?: string;
  /** Body paragraph. Defaults per `variant`. */
  body?: string;
  /**
   * Hidden-by-default JSON / stack block. Only useful when an installer or
   * support tech needs to copy something — never the first thing the user
   * sees.
   */
  details?: unknown;
  onRetry?: () => void;
  /**
   * When set, the screen auto-fires onRetry every N seconds. Used for the
   * offline variant so the kiosk recovers without anyone walking up to it.
   */
  autoRetrySeconds?: number;
}

/**
 * Surfaced when the runtime can't load or validate a config. Per the
 * "fail to Core, never to crash" principle, the launcher must remain
 * recoverable — the screen leads with plain-English copy and a big
 * retry button. The JSON details collapse is for installers, not the
 * person standing in front of the kiosk.
 */
export function ConfigErrorScreen({
  variant = 'invalidConfig',
  title,
  body,
  details,
  onRetry,
  autoRetrySeconds,
}: ConfigErrorScreenProps): JSX.Element {
  const copy = COPY[variant];
  const finalTitle = title ?? copy.title;
  const finalBody = body ?? copy.body;

  // Auto-retry countdown (offline variant). Resets if onRetry/seconds change.
  const [secondsLeft, setSecondsLeft] = useState<number | null>(
    autoRetrySeconds && onRetry ? autoRetrySeconds : null,
  );
  useEffect(() => {
    if (!autoRetrySeconds || !onRetry) {
      setSecondsLeft(null);
      return;
    }
    setSecondsLeft(autoRetrySeconds);
    const interval = window.setInterval(() => {
      setSecondsLeft((prev) => {
        if (prev === null) return null;
        if (prev <= 1) {
          onRetry();
          return autoRetrySeconds;
        }
        return prev - 1;
      });
    }, 1000);
    return () => window.clearInterval(interval);
  }, [autoRetrySeconds, onRetry]);

  return (
    <div
      className={`config-error config-error--${variant}`}
      role="alert"
      data-testid="config-error"
    >
      <div className="config-error__icon" aria-hidden="true">
        {copy.icon}
      </div>
      <h1 className="config-error__title">{finalTitle}</h1>
      <p className="config-error__message">{finalBody}</p>
      {onRetry && (
        <button
          type="button"
          className="config-error__retry"
          onClick={onRetry}
          data-testid="config-error-retry"
        >
          Try again
        </button>
      )}
      {secondsLeft !== null && (
        <p className="config-error__countdown" aria-live="polite">
          Retrying automatically in {secondsLeft}s…
        </p>
      )}
      {details !== undefined && (
        <details className="config-error__details">
          <summary>Technical details (for support)</summary>
          <pre>{safeStringify(details)}</pre>
        </details>
      )}
    </div>
  );
}

interface VariantCopy {
  title: string;
  body: string;
  icon: ReactNode;
}

const COPY: Record<ConfigErrorVariant, VariantCopy> = {
  offline: {
    title: "Can't reach the network",
    body: "This device is offline. We'll keep trying — check the Wi-Fi or cellular connection, or pull this device back into range.",
    icon: <CloudOffIcon />,
  },
  serverError: {
    title: 'The fleet server is unreachable',
    body: "We can talk to the network, but the configuration server isn't answering right now. This usually clears up on its own — try again in a moment.",
    icon: <ServerErrorIcon />,
  },
  invalidConfig: {
    title: "This device's configuration needs attention",
    body: "We loaded a configuration, but couldn't use it. An admin needs to republish from the portal. The launcher will keep trying.",
    icon: <WarningIcon />,
  },
  tampered: {
    title: 'This launcher bundle failed its security check',
    body: "For your protection, this device won't load — the bundle isn't properly signed, or its contents don't match what the server published. Re-deploy a fresh bundle from the portal, then tap Try again.",
    icon: <ShieldAlertIcon />,
  },
  crash: {
    title: 'The launcher needs to restart',
    body: 'Something unexpected happened. Tap below to reload — this usually fixes it.',
    icon: <WarningIcon />,
  },
};

function CloudOffIcon(): JSX.Element {
  return (
    <svg viewBox="0 0 24 24" width="96" height="96" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 3l18 18" />
      <path d="M8.5 6.5A6 6 0 0 1 19 9.5a4 4 0 0 1 2 7.5" />
      <path d="M16 16H8a4 4 0 0 1-3.6-5.7" />
    </svg>
  );
}

function ServerErrorIcon(): JSX.Element {
  return (
    <svg viewBox="0 0 24 24" width="96" height="96" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="4" width="18" height="6" rx="1.5" />
      <rect x="3" y="14" width="18" height="6" rx="1.5" />
      <path d="M7 7h.01M7 17h.01" />
      <path d="M16 7l4 4M20 7l-4 4" />
    </svg>
  );
}

function ShieldAlertIcon(): JSX.Element {
  return (
    <svg viewBox="0 0 24 24" width="96" height="96" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 3l8 3v6c0 4.5-3.2 7.6-8 9-4.8-1.4-8-4.5-8-9V6l8-3z" />
      <path d="M12 9v4" />
      <path d="M12 16h.01" />
    </svg>
  );
}

function WarningIcon(): JSX.Element {
  return (
    <svg viewBox="0 0 24 24" width="96" height="96" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 3l10 18H2L12 3z" />
      <path d="M12 10v5" />
      <path d="M12 18h.01" />
    </svg>
  );
}

function safeStringify(value: unknown): string {
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}
